import React, { useLayoutEffect, useCallback, useEffect, useRef } from 'react';
import { Editor, type Element, Transforms, Range } from 'slate';
import {
  useSlateStatic,
  useSelected,
  ReactEditor,
  useReadOnly,
} from 'slate-react';
import { useController } from '@aha-app/mvc';
import AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import styles from './Resizer.module.css';
import classnames from 'classnames';
import DragHandle, { type DragHandlePosition } from './DragHandle';
import PixelSize from './PixelSize';
import { debounce } from 'lodash';
import useEventHandler from '../useEventHandler';
import type { CustomElement } from '../../types/aha-editor';

const MIN_SIZE = 20;

function clampSize(currentSize: number, maxSize: number | undefined) {
  return Math.min(
    isNaN(currentSize) ? 0 : currentSize,
    !maxSize || isNaN(maxSize) ? Infinity : maxSize
  );
}

interface ResizerProps {
  width?: number;
  height?: number;
  originalWidth?: number;
  originalHeight?: number;
  // The element / slate node to resize.
  element: Extract<CustomElement, { resizer_width?: number }>;
  // The element is being actively resized.
  onResize?: (width: number, height: number) => void;
  // The element was resized.
  onResized?: (width: number, height: number) => void;
  // Whether the width:height ratio should be kept when resizing.
  keepRatio: boolean;
  // Images and videos can use height: auto because the media dictates the aspect ratio. But for embedded content, we cannot use this.
  useAutoHeight?: boolean;
  // Whether to show an indication of the current dimensions while resizing.
  showPixelSize?: boolean;
  // Whether the element supports live resizing. TODO: should this be in the schema?
  liveResize?: boolean;
  // Which handles should be displayed on the resizer.
  handles?: DragHandlePosition[];
  children: React.ReactNode;
  // Whether the resizer must force to fill all the horizontal space
  fullWidth?: boolean;
  minWidth?: number;
  minHeight?: number;
  // If true, only update width when dragging - height is determined by element's implicit dimensions
  widthOnly?: boolean;
  // If true, only update height when dragging - width is determined by element's implicit dimensions
  heightOnly?: boolean;
  // If true, preserve the explicitly set width in read-only mode instead of recalculating based on container size
  preserveWidthInReadOnly?: boolean;
  // Callback when a drag handle is clicked down.
  moveStartCallback?: () => void;
}

/*
 * RESIZER COMPONENT DOCUMENTATION
 *
 * This component manages multiple width/height values that serve different purposes.
 * Understanding these variables is crucial for working with the resizer logic:
 *
 * === WIDTH VARIABLES ===
 *
 * 1. `width` (prop)
 *    - The desired/target width passed from the parent component
 *    - Represents what the parent thinks the width should be
 *    - May come from persisted element data (resizer_width)
 *    - Can be undefined if not set
 *
 * 2. `originalWidth` (prop)
 *    - The intrinsic/natural width of the content (e.g., image's natural width)
 *    - Used for aspect ratio calculations when keepRatio=true
 *    - Typically set once when content is first loaded
 *    - Example: 800px for an 800x600 image
 *
 * 3. `localWidth` (state)
 *    - The current width being displayed/rendered by the resizer
 *    - Updated during drag operations and prop changes
 *    - This is what gets applied to the DOM element's style
 *    - In widthOnly mode: actively controlled, in heightOnly mode: left as 0
 *
 * 4. `localMaxWidth` (state)
 *    - The maximum allowed width based on container constraints
 *    - Calculated by measuring available space in the container
 *    - Prevents the element from overflowing its container
 *    - Updated when window resizes or container changes
 *
 * 5. `containerWidth` (computed)
 *    - Width of the editor container (editorContainer?.clientWidth)
 *    - Used in fullWidth mode to make element span full editor width
 *    - Different from localMaxWidth (which may be a smaller nested container)
 *
 * 6. `minWidth` (prop)
 *    - Minimum allowed width, prevents element from becoming too small
 *    - Defaults to MIN_SIZE (20px)
 *    - Enforced during all resize operations
 *
 * === HEIGHT VARIABLES ===
 *
 * 1. `height` (prop)
 *    - The desired/target height passed from the parent component
 *    - May come from persisted element data (resizer_height)
 *    - Can be undefined if not set
 *
 * 2. `originalHeight` (prop)
 *    - The intrinsic/natural height of the content (e.g., image's natural height)
 *    - Used for aspect ratio calculations when keepRatio=true
 *    - Example: 600px for an 800x600 image
 *
 * 3. `localHeight` (state)
 *    - The current height being displayed/rendered by the resizer
 *    - Updated during drag operations and prop changes
 *    - In heightOnly mode: actively controlled, in widthOnly mode: left as 0
 *
 * === RESIZING MODES ===
 *
 * 1. Normal Mode (widthOnly=false, heightOnly=false)
 *    - Both width and height are actively controlled by the resizer
 *    - localWidth and localHeight are both updated
 *    - Aspect ratio calculations may apply if keepRatio=true
 *
 * 2. widthOnly Mode (widthOnly=true)
 *    - Only width is controlled, height is determined by content's natural sizing
 *    - localWidth is updated, localHeight stays 0
 *    - Element uses CSS height: auto or intrinsic content height
 *    - Common for elements like link cards where height should adapt to content
 *
 * 3. heightOnly Mode (heightOnly=true)
 *    - Only height is controlled, width is determined by content's natural sizing
 *    - localHeight is updated, localWidth stays 0
 *    - Element uses CSS width: auto or intrinsic content width
 *
 * === SPECIAL MODES ===
 *
 * 1. fullWidth Mode (fullWidth=true)
 *    - Element should span the full width of its container
 *    - Uses containerWidth instead of user-dragged width
 *    - Can be combined with widthOnly/heightOnly modes
 *    - Triggers special handling in useEffect to call setDimensions
 *
 * 2. keepRatio Mode (keepRatio=true)
 *    - Maintains aspect ratio based on originalWidth/originalHeight
 *    - When width changes, height is recalculated: height = width * (originalHeight/originalWidth)
 *    - Disabled in widthOnly/heightOnly modes to preserve natural sizing
 *
 * === DATA FLOW ===
 *
 * 1. Props (width/height) → useEffect → setDimensions() → localWidth/localHeight → DOM styles
 * 2. Drag operations → handleMoved() → setDimensions() → localWidth/localHeight → DOM styles
 * 3. Container changes → calculateMaxWidth() → localMaxWidth → width clamping in setDimensions()
 * 4. Commit → Transforms.setNodes() → persisted to element.resizer_width/resizer_height
 *
 * === COMMON PITFALLS ===
 *
 * - Don't confuse localWidth (display state) with width (prop/target)
 * - In widthOnly mode, localHeight=0 but actual rendered height may be different
 * - localMaxWidth represents container constraints, not content constraints
 * - containerWidth (editor) vs localMaxWidth (immediate container) serve different purposes
 * - fullWidth mode needs special handling to trigger containerWidth calculation
 */

// Wrap an element in a container that shows handles for resizing.
const Resizer: React.FC<ResizerProps> = ({
  width,
  height,
  originalWidth,
  originalHeight,
  element,
  onResize,
  onResized,
  keepRatio,
  useAutoHeight = keepRatio,
  showPixelSize,
  liveResize,
  handles,
  children,
  fullWidth,
  minWidth = MIN_SIZE,
  minHeight = MIN_SIZE,
  widthOnly = false,
  heightOnly = false,
  moveStartCallback,
  preserveWidthInReadOnly = false,
}) => {
  const editor = useSlateStatic();
  const selected = useSelected();
  const readOnly = useReadOnly();
  const focused = true; // useFocused();
  const resizerRef = React.useRef<HTMLDivElement>(null);
  const Tag = Editor.isInline(editor, element as Element) ? 'span' : 'div';
  const [resizing, setResizing] = React.useState(false); // Local state for resizing of the element
  const { actionSetResizing, editorContainer } = useController(
    AhaEditorV2Controller
  );
  const [localWidth, setLocalWidth] = React.useState(width);
  const [localHeight, setLocalHeight] = React.useState(height);
  const [localMaxWidth, setLocalMaxWidth] = React.useState(0);

  const prevDimensionProps = useRef({
    width,
    height,
  });

  const { selection } = editor;
  const active =
    selected &&
    focused &&
    selection &&
    !readOnly &&
    Range.isCollapsed(selection);

  const containerWidth = editorContainer?.clientWidth;
  const setDimensions = useCallback(
    (
      width: number,
      height: number,
      commit: boolean,
      maxWidth = localMaxWidth
    ) => {
      height = clampSize(height, Infinity);

      // Handle fullWidth mode by using the editor container width
      if (fullWidth && containerWidth) {
        width = containerWidth;
      } else if (!heightOnly) {
        width = Math.max(Math.round(clampSize(width, maxWidth)), minWidth);
      }

      // Calculate height only when not in widthOnly mode
      if (keepRatio && !widthOnly && !heightOnly) {
        if (originalHeight && originalWidth) {
          const ratio = originalHeight / originalWidth;
          height = width * ratio;
        }
      } else if (!widthOnly) {
        height = Math.max(height, minHeight);
      }
      // Note: In widthOnly mode, height calculation is skipped to allow natural sizing
      height = Math.round(height);

      if (isNaN(height)) {
        console.error('Height is not a number');
      }

      // Update local state based on resizing mode
      // widthOnly: Only control width, let element determine natural height
      // heightOnly: Only control height, let element determine natural width
      // Normal: Control both dimensions
      if (widthOnly) {
        setLocalWidth(width);
        // Don't update localHeight - let the element determine its own height
      } else if (heightOnly) {
        setLocalHeight(height);
        // Don't update localWidth - let the element determine its own width
      } else {
        setLocalWidth(width);
        setLocalHeight(height);
      }

      if (commit) {
        setResizing(false);
        actionSetResizing(false);
        if (onResized) onResized(width, height);

        const path = ReactEditor.findPath(editor, element);
        const updates: any = {
          resizer_originalWidth: originalWidth,
          resizer_originalHeight: originalHeight,
        };

        // Only persist the dimensions that are being actively controlled
        // This prevents overwriting natural dimensions in widthOnly/heightOnly modes
        if (!heightOnly) {
          updates.resizer_width = width;
        }
        if (!widthOnly) {
          updates.resizer_height = height;
        }
        Transforms.setNodes(editor, updates, { at: path });
      } else {
        if (liveResize) {
          if (onResize) {
            onResize(width, height);
          }
        }
      }
    },
    [
      editor,
      element,
      keepRatio,
      liveResize,
      localMaxWidth,
      onResize,
      onResized,
      originalWidth,
      originalHeight,
      fullWidth,
      containerWidth,
      widthOnly,
      heightOnly,
    ]
  );

  useEffect(() => {
    if (
      width !== prevDimensionProps.current.width ||
      height !== prevDimensionProps.current.height ||
      fullWidth
    ) {
      prevDimensionProps.current.width = width;
      prevDimensionProps.current.height = height;

      // Handle dimension updates based on resizing mode
      // When fullWidth is enabled, always call setDimensions to calculate container width
      if (widthOnly) {
        if (fullWidth) {
          // Need setDimensions to calculate containerWidth for fullWidth mode
          setDimensions(width ?? 0, height ?? 0, false);
        } else {
          setLocalWidth(width ?? 0);
        }
      } else if (heightOnly) {
        if (fullWidth) {
          // Need setDimensions to calculate containerWidth for fullWidth mode
          setDimensions(width ?? 0, height ?? 0, false);
        } else {
          setLocalHeight(height ?? 0);
        }
      } else {
        setDimensions(width ?? 0, height ?? 0, false);
      }
    }
  }, [width, height, fullWidth, widthOnly, heightOnly, setDimensions]);

  // Don't allow the element to overflow the parent container, so we measure
  // the size of the parent and constrain the size of the element to that.
  // Calculate the maximum available width by measuring the container.
  const calculateMaxWidth = useCallback(() => {
    const measurementElement = resizerRef.current;
    if (!measurementElement) return;

    // Do not resize if we're in read-only and the parent element wants to preserve width
    if (readOnly && preserveWidthInReadOnly) return;

    // Temporarily set width to 100% to measure available space
    const oldWidth = measurementElement.style.width;

    // Temporarily set the width to a very large value to measure the full available
    // container width. We use 100cqw (rather than 100%) because the resizer may be
    // several levels deep in parent elements and we want to try to expand to the
    // full width of the editor content itself.
    // If we ever need to constrain this to some other container, we can easily add a
    // `container-type` to the CSS of the other parent element which would then use
    // _that_ container as the reference for 100cqw.
    measurementElement.style.width = '100cqw';

    const elementWidth = measurementElement.clientWidth;
    measurementElement.style.width = oldWidth;

    // If we couldn't compute the max width, don't set it.
    if (elementWidth === 0) {
      return;
    }

    setLocalMaxWidth(elementWidth);
    // Update the width based on the new max width (but not in widthOnly/heightOnly modes)
    if (!widthOnly && !heightOnly) {
      setDimensions(width ?? 0, height ?? 0, false, elementWidth);
    }
  }, [
    width,
    height,
    setDimensions,
    widthOnly,
    heightOnly,
    readOnly,
    preserveWidthInReadOnly,
  ]);

  // Modifying the window size might affect the max width.
  const onWindowResize = debounce(calculateMaxWidth, 100);
  useEventHandler('resize', () => onWindowResize(), window);

  // Calculate the maximum width when we first load.
  useLayoutEffect(() => {
    calculateMaxWidth();
  }, []);

  const handleMoveStart = () => {
    moveStartCallback?.();
    calculateMaxWidth();
    setResizing(true);
    actionSetResizing(true);
  };

  const handleMoved = (
    complete: boolean,
    position: DragHandlePosition,
    x: number,
    y: number
  ) => {
    if (!resizerRef.current) {
      return;
    }

    const rect = resizerRef.current.getBoundingClientRect();
    let width = 0;
    let height = 0;

    switch (position) {
      case 'w':
        width = rect.width - (x - rect.left);
        height = rect.height;
        break;
      case 'e':
        width = x - rect.left;
        height = rect.height;
        break;
      case 's':
        width = rect.width;
        height = y - rect.top;
        break;
      case 'se':
        width = x - rect.left;
        height = y - rect.top;
        break;
    }

    // In widthOnly mode, preserve current height
    if (widthOnly) {
      height = localHeight ?? 0;
    }

    // In heightOnly mode, preserve current width
    if (heightOnly) {
      width = localWidth ?? 0;
    }

    setDimensions(width, height, complete);
  };

  // Get the width to display in pixel size indicator
  // In heightOnly mode, localWidth is 0 so measure the actual rendered width
  const getDisplayWidth = () => {
    if (heightOnly && resizerRef.current) {
      return resizerRef.current.clientWidth;
    }
    return localWidth ?? 0;
  };

  // Get the height to display in pixel size indicator
  // In widthOnly mode, localHeight is 0 so measure the actual rendered height
  const getDisplayHeight = () => {
    if (widthOnly && resizerRef.current) {
      return resizerRef.current.clientHeight;
    }
    return localHeight ?? 0;
  };

  const pixelSize =
    showPixelSize && resizing ? (
      <PixelSize width={getDisplayWidth()} height={getDisplayHeight()} />
    ) : null;

  const handleComponents = active
    ? (handles ?? ['e']).map((position: DragHandlePosition) => (
        <DragHandle
          key={position}
          position={position}
          onMoveStart={handleMoveStart}
          onMove={(position, x, y) => handleMoved(false, position, x, y)}
          onMoved={(position, x, y) => handleMoved(true, position, x, y)}
        />
      ))
    : null;

  return (
    <Tag
      className={classnames(styles.resizer, {
        [styles.resizing]: resizing,
      })}
      style={{
        width: heightOnly ? undefined : localWidth,
        height:
          widthOnly || (keepRatio && useAutoHeight) ? undefined : localHeight,
      }}
      ref={resizerRef}
    >
      {children}
      {pixelSize}
      {handleComponents}
    </Tag>
  );
};

export default Resizer;
